import type { components } from '@scalty/contracts';
import type { StudioContext } from './studio';
import { collect } from './studio';

type S = components['schemas'];

/**
 * The name to show for a cited document, by the id a citation carries.
 *
 * A citation names its source with a `doc_…` handle — a memory collection's own document, which
 * is not the file the customer uploaded and not a data object either. Showing that handle to the
 * customer is showing them an internal identifier, which is what the browser walk found on both
 * the playground chips and the conversation page (W8-UI-02, W8-UI-03).
 *
 * Two hops resolve it. A document the platform projected from a dataset file is filed under
 * `data-object-<id>.markdown`, an identifier wearing a file extension, so its own `filename` is
 * no better than the handle; `sourceDataObjectId` leads back to the file the customer added and
 * its `displayName` is the name they gave it. A document with no source object keeps its own
 * filename, which in that case is a name somebody chose.
 *
 * A document whose id is absent from the map has no name to show. The surface then falls back to
 * what it can say honestly — a citation marker, or a count — never to the identifier.
 */
export async function citationDocumentNames(
  ctx: StudioContext,
  projectId: string | null,
): Promise<Record<string, string>> {
  if (projectId === null) return {};
  const [collections, uploaded] = await Promise.all([
    collect<S['KnowledgeBase']>(ctx, `/knowledge-bases?project=${encodeURIComponent(projectId)}`, 'knowledgeBases'),
    collect<S['DataObject']>(ctx, `/data-objects?project=${encodeURIComponent(projectId)}`, 'dataObjects'),
  ]);
  const uploadedNames = new Map(uploaded.map((object) => [object.id, object.displayName]));
  const names: Record<string, string> = {};
  for (const collection of collections) {
    const documents = await collect<S['Document']>(ctx, `/knowledge-bases/${encodeURIComponent(collection.id)}/documents`, 'documents');
    for (const document of documents) {
      const name = document.sourceDataObjectId === null
        ? document.filename
        : uploadedNames.get(document.sourceDataObjectId);
      if (name !== undefined) names[document.id] = name;
    }
  }
  return names;
}
